// Pointer ↔ time math for appointment drag-move and resize on the day/week
// time grids. All conversions are MEASURED from the rendered column element
// rather than derived from remPerMinute: the grid rows are `minmax(Xrem, 1fr)`
// and stretch beyond their rem minimum on tall viewports, so a computed rem→px
// factor would drift. Measuring absorbs zoom, tick interval, visible-hours and
// row stretching in one go.

import { CALENDAR_FINE_MINUTES } from "./calendar-time-axis";
import { isCancelledCalendarStatus } from "./calendar-utils";

export const SNAP_MINUTES = CALENDAR_FINE_MINUTES;
export const MIN_APPOINTMENT_MINUTES = 15;

// Matches the fine grid's leading header row (`1.75rem` in fineRowTemplate).
const COLUMN_HEADER_REM = 1.75;

/**
 * The grid's fixed leading header row in px — the strip above the time rows that
 * does NOT scale with zoom. Everything below it does.
 */
export function gridHeaderPx() {
  return COLUMN_HEADER_REM * getRootFontSizePx();
}

export function getRootFontSizePx() {
  const size = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(size) && size > 0 ? size : 16;
}

/**
 * Measure a rendered time column (an element carrying `data-time-col`, whose
 * rect spans the full fine grid including the 1.75rem header row) against the
 * time axis. Returns null when the column cannot be measured.
 */
export function measureColumnGeometry(columnEl, timeAxis) {
  if (!columnEl || !timeAxis?.totalMinutes) return null;
  const rect = columnEl.getBoundingClientRect();
  const headerPx = COLUMN_HEADER_REM * getRootFontSizePx();
  const bodyHeightPx = rect.height - headerPx;
  if (!(bodyHeightPx > 0)) return null;
  const pxPerMinute = bodyHeightPx / timeAxis.totalMinutes;
  return {
    rect,
    headerPx,
    pxPerMinute,
    minuteAtClientY(clientY) {
      return timeAxis.startMinutes + (clientY - rect.top - headerPx) / pxPerMinute;
    },
  };
}

/**
 * Elements that carry the vertical time geometry for a rendered grid.
 * `data-time-col` marks a day-view column — both a drop target and a geometry
 * source. The week view's event grid spans all seven days at once, so it can't
 * be a per-day drop target (its day droppables live in a sibling background
 * layer, not above the cards); it carries `data-time-grid` purely as the
 * geometry source. Both rects span the same 1.75rem header + fine grid.
 */
export const TIME_GEOMETRY_SELECTOR = "[data-time-col],[data-time-grid]";

/**
 * Nearest ancestor (or self) that can be measured by measureColumnGeometry.
 */
export function closestTimeGeometryElement(node) {
  if (!(node instanceof Element)) return null;
  return node.closest(TIME_GEOMETRY_SELECTOR);
}

export function snapMinutes(minutes) {
  return Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
}

/**
 * Clamp a snapped start (minutes since midnight) so the whole event stays
 * inside the visible window.
 */
export function clampStartMinutes(startMinutes, durationMinutes, axis) {
  const min = axis.startMinutes;
  const max = Math.max(min, axis.endMinutes - durationMinutes);
  return Math.min(Math.max(startMinutes, min), max);
}

/**
 * Recurring occurrences expand server-side into items sharing the series
 * master's ObjectID with a date suffix ("a-<hex>-YYYYMMDD", see the backend's
 * appointmentItemID). They cannot be edited individually.
 */
export function isRecurringOccurrenceId(id) {
  return /^a-[0-9a-f]{24}-\d{8}$/i.test(String(id || ""));
}

/**
 * Single gate for drag-move, resize and the editor's time fields. Appointments
 * without an addressable patient (NEW-patient bookings and phone-matched Plato
 * snapshots carry the zero patient id) are still editable: the update endpoint
 * accepts the zero id as its URL sentinel and skips the patient sanity check.
 */
export function canMutateAppointmentEvent(event) {
  if (!event || event.kind !== "appointment") return false;
  if (isCancelledCalendarStatus(event.status)) return false;
  if (isRecurringOccurrenceId(event.id)) return false;
  return true;
}
